import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './policy.module.css';

/**
 * 개인정보처리방침.
 *
 * 2026-10-09 처음 만들었다(토스페이먼츠 전자결제 심사가 사이트 하단 링크를 본다).
 * 적힌 내용은 **코드가 실제로 하는 일**에 맞춘 것이다. 아래가 바뀌면 이 문서도 고친다.
 *  - 견적요청 칸(이름·휴대폰·이메일·운행정보·메모)이 늘거나 줄 때
 *  - 문자(카페24)·메일·텔레그램 알림·AI 견적 초안(Anthropic)·결제 업체가 바뀔 때
 *  - 서버를 옮길 때 — 지금은 오라클 클라우드 **도쿄 리전**이라 «국외 이전» 에 적었다
 *  - 3번 보유 기간 — 서버 `PrivacyRetentionService` 가 실제로 지우는 기간과 같아야 한다
 */
export default function Privacy({ companyData = {} }) {
  useEffect(() => {
    const titleElement = document.querySelector('title');
    titleElement.innerHTML = `이케이하나관광-개인정보처리방침`;
    // 하단 링크로 들어오므로, 그대로 두면 새 화면이 맨 아래에서 열린다.
    window.scrollTo(0, 0);
  }, []);

  const officer = companyData.infoHandlerName || '';
  const tel = companyData.tel || '';
  const email = companyData.email || '';

  return (
    <main className={styles.policy}>
      <h1 className={styles.title}>개인정보처리방침</h1>

      <p className={styles.intro}>
        주식회사 이케이투어(이하 «회사», 브랜드명 이케이하나관광)는 「개인정보 보호법」 등 관련 법령을 지키며,
        고객님의 개인정보를 아래와 같이 처리합니다.
      </p>

      <section className={styles.section}>
        <h2 className={styles.heading}>1. 처리하는 개인정보 항목</h2>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>구분</th><th>항목</th></tr>
            </thead>
            <tbody>
              <tr>
                <th>견적요청 (필수)</th>
                <td>이름, 휴대폰 번호, 운행 정보(출발·도착 일시와 장소, 인원, 차량 종류·대수)</td>
              </tr>
              <tr>
                <th>견적요청 (선택)</th>
                <td>이메일(이메일로 견적서 받기를 고른 경우 필수), 요청사항(메모)</td>
              </tr>
              <tr>
                <th>계약·결제</th>
                <td>
                  입금자명, 결제 승인 정보(카드사·승인번호·결제금액 등), 세금계산서·현금영수증 발급 정보
                  (사업자등록번호 또는 휴대폰 번호)
                </td>
              </tr>
              <tr>
                <th>자동 수집</th>
                <td>접속 IP 주소, 접속 일시, 방문 경로, 브라우저 정보</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className={styles.text}>
          카드번호 등 결제 수단 정보는 결제대행사가 직접 처리하며 회사는 저장하지 않습니다.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>2. 처리 목적</h2>
        <ul className={styles.list}>
          <li>전세버스 견적 상담과 견적서 발송(문자·이메일)</li>
          <li>운송 계약의 체결·이행: 배차, 운전기사 확인 연락, 요금 결제와 정산, 증빙 발급</li>
          <li>나의견적확인을 위한 휴대폰 문자 본인확인</li>
          <li>문의·불만 처리와 분쟁 대응</li>
          <li>부정 이용 방지와 사이트 보안, 접속 통계</li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>3. 보유 및 이용 기간</h2>
        <p className={styles.text}>
          처리 목적을 달성하면 지체 없이 파기하며, 아래 기간이 지나면 매일 자동으로 파기합니다.
        </p>
        <ul className={styles.list}>
          <li>견적요청 정보(이름·휴대폰 번호·이메일·요청사항): 접수일로부터 1년</li>
          <li>견적 안내 문자·이메일 발송 기록: 발송일로부터 1년</li>
          <li>견적서·계약서·대금결제 기록: 5년 (전자상거래법 — 계약·청약철회, 대금결제 기록)</li>
          <li>소비자의 불만 또는 분쟁처리에 관한 기록: 3년 (전자상거래법)</li>
          <li>세금계산서 등 거래 증빙: 5년 (국세기본법)</li>
          <li>접속 기록(IP 주소): 3개월 (통신비밀보호법)</li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>4. 제3자 제공</h2>
        <p className={styles.text}>
          회사는 고객님의 개인정보를 외부에 제공하지 않습니다. 다만 운송 계약 이행을 위해 아래와 같이 제공합니다.
        </p>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>제공받는 자</th><th>목적</th><th>항목</th><th>보유 기간</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>배차된 운수회사 및 운전기사</td>
                <td>운행 전 확인 연락, 운행</td>
                <td>이름, 휴대폰 번호, 운행 정보</td>
                <td>운행 종료 시까지</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>5. 처리 위탁</h2>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>수탁자</th><th>위탁 업무</th></tr>
            </thead>
            <tbody>
              <tr><td>주식회사 카페24</td><td>견적 안내·본인확인 문자 발송</td></tr>
              <tr><td>네이버 주식회사, Google LLC</td><td>견적서 이메일 발송</td></tr>
              <tr><td>토스페이먼츠 주식회사</td><td>카드 결제 처리(결제 링크)</td></tr>
              <tr><td>Oracle Corporation</td><td>서버 운영(홈페이지·데이터 보관)</td></tr>
              <tr><td>Telegram Messenger Inc.</td><td>새 견적요청을 담당자에게 알림</td></tr>
              <tr><td>Anthropic, PBC</td><td>견적 초안 작성 보조(이름·연락처는 보내지 않음)</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>6. 국외 이전</h2>
        <p className={styles.text}>
          서비스 운영을 위해 아래와 같이 개인정보가 국외로 이전(보관)됩니다. 이전은 정보통신망을 통해 수시로 이루어집니다.
          국외 이전을 원하지 않으시면 견적요청 대신 전화({tel})로 상담하실 수 있습니다.
        </p>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>이전받는 자 (국가)</th><th>항목</th><th>목적·보유 기간</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Oracle Corporation (일본, 도쿄 데이터센터)</td>
                <td>1번의 모든 항목</td>
                <td>서버 보관 / 3번의 보유 기간</td>
              </tr>
              <tr>
                <td>Telegram Messenger Inc. (아랍에미리트 등)</td>
                <td>이름, 휴대폰 번호, 운행 정보, 요청사항</td>
                <td>담당자 알림 / 담당자가 알림을 지울 때까지</td>
              </tr>
              <tr>
                <td>Google LLC (미국) — Gmail 로 발송하는 경우</td>
                <td>이름, 이메일, 견적 내용</td>
                <td>견적서 발송 / 발송 기록 보관 기간</td>
              </tr>
              <tr>
                <td>Anthropic, PBC (미국)</td>
                <td>운행 정보, 요청사항</td>
                <td>견적 초안 작성 / 업체 정책에 따라 30일 이내 삭제</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>7. 파기 절차와 방법</h2>
        <p className={styles.text}>
          보유 기간이 지난 개인정보는 매일 새벽 자동으로 파기합니다. 전자 파일은 복구할 수 없는 방법으로 지우고,
          방문 통계에 쓰는 접속 IP는 원래 값을 알아낼 수 없는 익명값으로 바꿉니다. 종이 문서는 분쇄하거나 소각합니다.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>8. 고객님의 권리</h2>
        <p className={styles.text}>
          고객님은 언제든지 개인정보의 열람·정정·삭제·처리정지를 요구하실 수 있습니다.
          견적요청은 <Link className={styles.link} to='/my'>나의견적확인</Link>에서 직접 고치거나 취소할 수 있고,
          그 밖의 요구는 아래 보호책임자에게 전화·이메일로 하시면 지체 없이 처리합니다.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>9. 안전성 확보 조치</h2>
        <ul className={styles.list}>
          <li>전 구간 암호화 통신(HTTPS)</li>
          <li>관리자 화면 접근 통제와 접속 기록 보관</li>
          <li>공개 견적 목록의 이름·연락처 가림 처리, 본인 견적은 문자 인증 후에만 열람</li>
          <li>데이터 정기 백업과 서버 보안 업데이트</li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>10. 쿠키</h2>
        <p className={styles.text}>
          홈페이지는 로그인 기능이 없으며, 고객님을 추적하는 광고 쿠키를 쓰지 않습니다.
          브라우저 설정에서 쿠키 저장을 거부하셔도 견적요청을 이용하실 수 있습니다.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>11. 개인정보 보호책임자</h2>
        <ul className={styles.list}>
          <li>보호책임자: {officer}</li>
          <li>전화: {tel}</li>
          <li>이메일: {email}</li>
        </ul>
        <p className={styles.text}>
          개인정보 침해에 대한 신고·상담은 개인정보침해신고센터(국번 없이 118, privacy.kisa.or.kr),
          개인정보분쟁조정위원회(1833-6972, www.kopico.go.kr)에도 하실 수 있습니다.
        </p>
      </section>

      <p className={styles.effective}>시행일: 2026년 10월 9일</p>
    </main>
  );
}
